import {
    CancelledTruckDeliveryIcon,
    FlagIcon,
    FootIcon,
    PhoneIcon,
    TruckDeliveryIcon,
    VisitedHandshakeIcon
} from 'assets'
import { GenderIcon } from 'components/client/GenderIcon'
import { abbreviateName, numberFormatter, constants, formatPhone } from 'helpers'
import { getGenderAgeProfile } from 'pages/village/helpers/villageClientHelpers'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/colors'
import { useAnalytics } from 'hooks'

type CardVillagePotentialClientProps = {
    client_code: string
    fullname: string
    phone?: string
    recent_year_delivered: number
    balance: number
    sex: string
    age: number
    isVisited: boolean
    duplicate: boolean
    isNotInterested: boolean
    onClickHandler: (clientCode: string) => void
}

export const CardVillagePotentialClient = ({
    client_code: clientCode,
    fullname,
    phone,
    recent_year_delivered: recentYearDelivered,
    sex,
    age,
    isVisited,
    onClickHandler,
    ...props
}: CardVillagePotentialClientProps) => {
    const { t } = useTranslation()
    const { trackEvent } = useAnalytics()

    const genderIconUrl = useMemo(
        () => getGenderAgeProfile(t, t(sex), age),
        [t, sex, age]
    )

    const phoneHandler = (e: React.MouseEvent) => {
        e.stopPropagation()
        trackEvent({
            category: constants.ANALYTICS.VILLAGE_PAGE,
            action: constants.ANALYTICS.ACTIONS.PHONE_CALL
        })
        window.location.href = `tel:${phone}`
    }

    const abbreviatedCircleName = (fullname: string) => (
        <div className="card-client-abbr card-client-abbr--name">
            {abbreviateName(fullname)}
        </div>
    )

    return (
        <div
            className="card--potential-client-container"
            onClick={() => onClickHandler(clientCode)}
        >
            <div className="card--potential-client-content-container">
                <div>
                    {props.duplicate || props.isNotInterested ? (
                        props.duplicate ? (
                            <FlagIcon height="60" width="60" />
                        ) : (
                            props.isNotInterested && (
                                <div className="card--not-interested-container">
                                    <div className="card--not-interested"></div>
                                </div>
                            )
                        )
                    ) : sex && age ? (
                        <GenderIcon src={genderIconUrl} size={60} />
                    ) : (
                        abbreviatedCircleName(fullname)
                    )}
                </div>
                <div>
                    <div className="card--content-bottom-margin card--content-id-container">
                        <p className="card--potential-client-content-text">
                            ID:{clientCode}
                        </p>
                        {props.duplicate || props.isNotInterested ? (
                            <></>
                        ) : isVisited ? (
                            <div
                                aria-label="visited-handshake-icon"
                                className="display-flex"
                            >
                                <VisitedHandshakeIcon
                                    key="caret-icon"
                                    color={colors.primary}
                                    height="26"
                                    width="26"
                                />
                            </div>
                        ) : (
                            <div
                                aria-label="foot-icon"
                                className="display-flex"
                            >
                                <FootIcon
                                    color={colors.maroon}
                                    strokeColor={colors.maroon}
                                    key={'foot-icon'}
                                    height="24"
                                    width="24"
                                />
                            </div>
                        )}
                    </div>
                    <div className="card--content-bottom-margin">
                        <p className="card--potential-client-name">
                            {fullname}
                        </p>
                        {phone ? (
                            <p className="card--potential-client-phone">
                                {formatPhone(phone)}
                            </p>
                        ) : null}
                    </div>
                    {props.balance ? (
                        <div>
                            <p className="card--potential-client-content-text card--available-potential-client-balance-color">
                                {`${t('village.available')}: ${numberFormatter(
                                    props.balance
                                )}`}
                            </p>
                        </div>
                    ) : (
                        ''
                    )}
                    <div className="card--potential-client-delivery-container">
                        {recentYearDelivered ? (
                            <TruckDeliveryIcon
                                width={'20'}
                                height={'20'}
                                color={colors.black}
                            />
                        ) : (
                            <CancelledTruckDeliveryIcon />
                        )}
                        <p
                            className="card--potential-client-content-text"
                            aria-label="year-delivered"
                        >
                            {recentYearDelivered}
                        </p>
                    </div>
                </div>
            </div>
            {phone && (
                <div
                    onClick={phoneHandler}
                    aria-label="phone-client-icon"
                    className="display-flex"
                >
                    <PhoneIcon
                        width="28"
                        height="28"
                        color={colors.maroon}
                        fillColor={colors.maroon}
                    />
                </div>
            )}
        </div>
    )
}
